import { T } from '@/lib/theme'
import { fmtUsdCompact } from '@/lib/utils'

export const CHART_W = 1000
export const CHART_H = 220
export const PAD = 6
const Y_AXIS_W = 56

/// Hand-rolled SVG line(s) over a shared scale, plus real x-axis date ticks - shared by every
/// small time-series chart in this app (Stats' Deposit/Borrow chart, Portfolio's position chart)
/// so the tick-placement/date-formatting logic exists in one place, not copy-pasted per chart.
/// Exported (along with the CHART_W/CHART_H/PAD constants above) so a caller needing an overlay
/// MiniLineChart itself doesn't support - VaultDetailSection.jsx's range-band price chart - can
/// share the exact same coordinate scale rather than reimplementing it slightly differently.
export function buildLine(values, min, max) {
  const n = values.length
  if (n === 0) return ''
  const range = max - min || 1
  const stepX = n > 1 ? (CHART_W - PAD * 2) / (n - 1) : 0
  return values
    .map((v, i) => {
      const x = PAD + i * stepX
      const y = CHART_H - PAD - ((v - min) / range) * (CHART_H - PAD * 2)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
}

/// Evenly-spaced indices into a series for x-axis date ticks - always includes the first and
/// last point (the range's real endpoints), with up to `count - 2` more spread between them.
/// Ticks land on actual series points rather than a fixed calendar interval, consistent with
/// this app's "no fabricated data" rule (a fixed-interval tick would need to interpolate between
/// real points).
export function pickTickIndices(n, count = 5) {
  if (n <= 1) return [0]
  const c = Math.min(count, n)
  const idx = new Set()
  for (let i = 0; i < c; i++) idx.add(Math.round((i * (n - 1)) / (c - 1)))
  return [...idx]
}

/// Time-of-day when the whole series fits inside one day (a fresh series is often minutes old,
/// where a date alone would repeat the same label on every tick); calendar date once points span
/// more than a day.
export function fmtChartDate(ms, spanMs) {
  const date = new Date(ms)
  return spanMs < 24 * 60 * 60 * 1000
    ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/// `series`: array of points with a `.t` (ms timestamp), oldest first. `lines`: one or more
/// `{ values, color }`, each `values` the same length as `series`, sharing one min/max scale so
/// multiple lines stay directly visually comparable. Price axis ticks land at fixed fractions of
/// the shared min/max range (top/middle/bottom), not on real data points the way date ticks do -
/// unlike a date, a price axis is just labeling the existing linear scale, not asserting a real
/// observation happened at that exact value.
export function MiniLineChart({ series, lines, height = CHART_H, formatY = fmtUsdCompact, includeZero = true }) {
  const allValues = lines.flatMap((l) => l.values)
  const min = includeZero ? Math.min(0, ...allValues) : Math.min(...allValues)
  const max = includeZero ? Math.max(1, ...allValues) : Math.max(...allValues)
  const tickIndices = pickTickIndices(series.length, 5)
  const spanMs = series[series.length - 1].t - series[0].t
  const stepX = series.length > 1 ? (CHART_W - PAD * 2) / (series.length - 1) : 0
  const yTicks = [max, (max + min) / 2, min]

  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ position: 'relative', width: Y_AXIS_W, flexShrink: 0, height }}>
        {yTicks.map((v, i) => (
          <span key={i} style={{
            position: 'absolute', right: 0, top: `${(i / (yTicks.length - 1)) * 100}%`,
            transform: i === 0 ? 'translateY(0)' : i === yTicks.length - 1 ? 'translateY(-100%)' : 'translateY(-50%)',
            color: T.textDim, fontFamily: T.fontM, fontSize: 10, whiteSpace: 'nowrap',
          }}>
            {formatY(v)}
          </span>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
          {lines.map((l, i) => (
            <path key={i} d={buildLine(l.values, min, max)} fill="none" stroke={l.color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          ))}
        </svg>
        <div style={{ position: 'relative', height: 16, marginTop: 8 }}>
          {tickIndices.map((i) => {
            const xPct = ((PAD + i * stepX) / CHART_W) * 100
            const isFirst = i === 0
            const isLast = i === series.length - 1
            return (
              <span key={i} style={{
                position: 'absolute', left: `${xPct}%`, top: 0,
                transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                color: T.textDim, fontFamily: T.fontM, fontSize: 10, whiteSpace: 'nowrap',
              }}>
                {fmtChartDate(series[i].t, spanMs)}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}
